/**
 * The formula language used by formula fields and formula validation rules.
 *
 * A small, spreadsheet-style expression language: literals, field references by API name,
 * operators (+ - * / & = <> < <= > >= && || !) and a fixed set of functions. Expressions are
 * parsed and type-checked here; the API compiles the typed tree to SQL (formula fields are
 * computed when records are read, so lists, filters, sorting and reports see current values),
 * and `evaluateFormula` runs the same tree in memory (validation rules, workflow conditions).
 * Both follow SQL's null rules, so the two give the same answers.
 *
 * There is no NULL literal (use ISBLANK / BLANKVALUE) and no access to other records: a formula
 * reads the record's own fields, including roll-up summaries and other formulas.
 */
import type { FieldDef, FieldType } from './objects';

export type FormulaType = 'number' | 'text' | 'date' | 'boolean';
export const FORMULA_RETURN_TYPES = ['number', 'currency', 'percent', 'text', 'date', 'boolean'] as const;
export type FormulaReturnType = (typeof FORMULA_RETURN_TYPES)[number];

export const FORMULA_MAX_LENGTH = 3000;
/** Size limit of a formula including the formulas it references (each is inlined when compiled). */
export const FORMULA_MAX_NODES = 500;
/** How deep formulas may reference other formulas. */
export const FORMULA_MAX_CHAIN = 5;
/** Date arithmetic is limited to ±100,000 days (larger offsets give blank). */
export const FORMULA_MAX_DAY_OFFSET = 100_000;

export type BinaryOp = '+' | '-' | '*' | '/' | '&' | '=' | '<>' | '<' | '<=' | '>' | '>=';

/** A type-checked expression tree. Field nodes carry the referenced field's definition. */
export type FormulaNode =
  | { k: 'num'; v: number; t: 'number' }
  | { k: 'str'; v: string; t: 'text' }
  | { k: 'bool'; v: boolean; t: 'boolean' }
  | { k: 'field'; name: string; t: FormulaType; field: FieldDef }
  | { k: 'neg'; a: FormulaNode; t: 'number' }
  | { k: 'bin'; op: BinaryOp; a: FormulaNode; b: FormulaNode; t: FormulaType }
  | { k: 'call'; fn: string; args: FormulaNode[]; t: FormulaType };

/** What a formula field carries in the schema. `ast` is absent (and `error` set) when it no longer compiles. */
export interface FormulaInfo {
  expression: string;
  returnType: FormulaReturnType;
  ast?: FormulaNode;
  /** Node count including referenced formulas. */
  size?: number;
  error?: string;
}

export class FormulaError extends Error {
  constructor(message: string, readonly position: number | null = null) {
    super(position === null ? message : `${message} (at character ${position + 1})`);
    this.name = 'FormulaError';
  }
}

// ---------------------------------------------------------------------------
// Tokens and parsing (untyped tree)
// ---------------------------------------------------------------------------

type Tok =
  | { k: 'num'; v: number; p: number }
  | { k: 'str'; v: string; p: number }
  | { k: 'id'; v: string; p: number }
  | { k: 'op'; v: string; p: number }
  | { k: 'eof'; p: number };

const OPS = ['<=', '>=', '<>', '!=', '==', '&&', '||', '+', '-', '*', '/', '&', '=', '<', '>', '(', ')', ',', '!'];

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) { i++; continue; }
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(src[i + 1] ?? ''))) {
      const m = /^[0-9]*\.?[0-9]+(?:[eE][+-]?[0-9]+)?/.exec(src.slice(i))!;
      const v = Number(m[0]);
      if (!Number.isFinite(v)) throw new FormulaError('Number too large', i);
      out.push({ k: 'num', v, p: i });
      i += m[0].length;
      continue;
    }
    if (c === '"' || c === "'") {
      let s = '';
      let j = i + 1;
      for (;;) {
        if (j >= src.length) throw new FormulaError('Text is missing its closing quote', i);
        const d = src[j]!;
        if (d === '\\') {
          const e = src[j + 1];
          if (e === undefined) throw new FormulaError('Text is missing its closing quote', i);
          s += e === 'n' ? '\n' : e === 't' ? '\t' : e;
          j += 2;
          continue;
        }
        if (d === c) break;
        s += d;
        j++;
      }
      out.push({ k: 'str', v: s, p: i });
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!;
      out.push({ k: 'id', v: m[0], p: i });
      i += m[0].length;
      continue;
    }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (!op) throw new FormulaError(`Unexpected character "${c}"`, i);
    out.push({ k: 'op', v: op, p: i });
    i += op.length;
  }
  out.push({ k: 'eof', p: src.length });
  return out;
}

type Raw =
  | { k: 'num'; v: number; p: number }
  | { k: 'str'; v: string; p: number }
  | { k: 'bool'; v: boolean; p: number }
  | { k: 'field'; name: string; p: number }
  | { k: 'neg'; a: Raw; p: number }
  | { k: 'bin'; op: BinaryOp; a: Raw; b: Raw; p: number }
  | { k: 'call'; fn: string; args: Raw[]; p: number };

const MAX_PARSE_DEPTH = 40;

function parse(src: string): Raw {
  if (!src.trim()) throw new FormulaError('Enter a formula');
  if (src.length > FORMULA_MAX_LENGTH) throw new FormulaError(`A formula can be up to ${FORMULA_MAX_LENGTH} characters`);
  const toks = tokenize(src);
  let pos = 0;
  let depth = 0;
  const peek = () => toks[pos]!;
  const isOp = (...vs: string[]) => { const t = peek(); return t.k === 'op' && vs.includes(t.v); };
  const take = () => toks[pos++]!;
  const expect = (v: string) => {
    const t = peek();
    if (t.k !== 'op' || t.v !== v) throw new FormulaError(`Expected "${v}"`, t.p);
    pos++;
  };
  const nest = <T>(fn: () => T): T => {
    if (++depth > MAX_PARSE_DEPTH) throw new FormulaError('The formula is nested too deeply', peek().p);
    try { return fn(); } finally { depth--; }
  };

  const or = (): Raw => nest(() => {
    let a = and();
    while (isOp('||')) { const p = take().p; a = { k: 'call', fn: 'OR', args: [a, and()], p }; }
    return a;
  });
  const and = (): Raw => {
    let a = cmp();
    while (isOp('&&')) { const p = take().p; a = { k: 'call', fn: 'AND', args: [a, cmp()], p }; }
    return a;
  };
  const cmp = (): Raw => {
    const a = concat();
    if (isOp('=', '==', '<>', '!=', '<', '<=', '>', '>=')) {
      const t = take() as { v: string; p: number };
      const op = (t.v === '==' ? '=' : t.v === '!=' ? '<>' : t.v) as BinaryOp;
      const b = concat();
      if (isOp('=', '==', '<>', '!=', '<', '<=', '>', '>=')) throw new FormulaError('Combine comparisons with AND() or OR()', peek().p);
      return { k: 'bin', op, a, b, p: t.p };
    }
    return a;
  };
  const concat = (): Raw => {
    let a = add();
    while (isOp('&')) { const p = take().p; a = { k: 'bin', op: '&', a, b: add(), p }; }
    return a;
  };
  const add = (): Raw => {
    let a = mul();
    while (isOp('+', '-')) { const t = take() as { v: BinaryOp; p: number }; a = { k: 'bin', op: t.v, a, b: mul(), p: t.p }; }
    return a;
  };
  const mul = (): Raw => {
    let a = unary();
    while (isOp('*', '/')) { const t = take() as { v: BinaryOp; p: number }; a = { k: 'bin', op: t.v, a, b: unary(), p: t.p }; }
    return a;
  };
  const unary = (): Raw => nest(() => {
    if (isOp('-')) { const p = take().p; return { k: 'neg', a: unary(), p }; }
    if (isOp('!')) { const p = take().p; return { k: 'call', fn: 'NOT', args: [unary()], p }; }
    return primary();
  });
  const primary = (): Raw => {
    const t = take();
    if (t.k === 'num') return { k: 'num', v: t.v, p: t.p };
    if (t.k === 'str') return { k: 'str', v: t.v, p: t.p };
    if (t.k === 'op' && t.v === '(') { const e = or(); expect(')'); return e; }
    if (t.k === 'id') {
      const upper = t.v.toUpperCase();
      if (isOp('(')) {
        take();
        const args: Raw[] = [];
        if (!isOp(')')) {
          do { args.push(or()); } while (isOp(',') && take());
        }
        expect(')');
        return { k: 'call', fn: upper, args, p: t.p };
      }
      if (upper === 'TRUE' || upper === 'FALSE') return { k: 'bool', v: upper === 'TRUE', p: t.p };
      if (upper === 'NULL') throw new FormulaError('There is no NULL value: use ISBLANK() or BLANKVALUE()', t.p);
      return { k: 'field', name: t.v, p: t.p };
    }
    if (t.k === 'eof') throw new FormulaError('The formula ends too early', t.p);
    throw new FormulaError(`Unexpected "${t.k === 'op' ? t.v : ''}"`, t.p);
  };

  const tree = or();
  if (peek().k !== 'eof') throw new FormulaError('Unexpected text after the end of the formula', peek().p);
  return tree;
}

// ---------------------------------------------------------------------------
// Functions
// ---------------------------------------------------------------------------

export interface FormulaFunctionDoc { name: string; syntax: string; description: string; validationOnly?: boolean }

/** Function reference for the formula editor. */
export const FORMULA_FUNCTIONS: FormulaFunctionDoc[] = [
  { name: 'IF', syntax: 'IF(condition, value_if_true, value_if_false)', description: 'Chooses one of two values of the same type.' },
  { name: 'AND', syntax: 'AND(a, b, …)', description: 'True when every condition is true (also written a && b).' },
  { name: 'OR', syntax: 'OR(a, b, …)', description: 'True when any condition is true (also written a || b).' },
  { name: 'NOT', syntax: 'NOT(condition)', description: 'Reverses a condition (also written !condition).' },
  { name: 'ISBLANK', syntax: 'ISBLANK(value)', description: 'True when the value is empty.' },
  { name: 'BLANKVALUE', syntax: 'BLANKVALUE(value, fallback)', description: 'The value, or the fallback when it is empty.' },
  { name: 'ROUND', syntax: 'ROUND(number, digits)', description: 'Rounds half away from zero to 0–10 decimal places (digits must be a whole number).' },
  { name: 'FLOOR', syntax: 'FLOOR(number)', description: 'Rounds down to a whole number.' },
  { name: 'CEILING', syntax: 'CEILING(number)', description: 'Rounds up to a whole number.' },
  { name: 'ABS', syntax: 'ABS(number)', description: 'The number without its sign.' },
  { name: 'MOD', syntax: 'MOD(number, divisor)', description: 'The remainder of a division (blank when dividing by zero).' },
  { name: 'MIN', syntax: 'MIN(a, b, …)', description: 'The smallest of the numbers or dates, ignoring blanks.' },
  { name: 'MAX', syntax: 'MAX(a, b, …)', description: 'The largest of the numbers or dates, ignoring blanks.' },
  { name: 'LEN', syntax: 'LEN(text)', description: 'The number of characters (0 when blank).' },
  { name: 'UPPER', syntax: 'UPPER(text)', description: 'Text in capitals.' },
  { name: 'LOWER', syntax: 'LOWER(text)', description: 'Text in lower case.' },
  { name: 'TRIM', syntax: 'TRIM(text)', description: 'Removes spaces at the start and end.' },
  { name: 'LEFT', syntax: 'LEFT(text, count)', description: 'The first characters (count must be a whole number).' },
  { name: 'RIGHT', syntax: 'RIGHT(text, count)', description: 'The last characters (count must be a whole number).' },
  { name: 'CONTAINS', syntax: 'CONTAINS(text, part)', description: 'True when the text contains the part (case-sensitive).' },
  { name: 'BEGINS', syntax: 'BEGINS(text, start)', description: 'True when the text starts with the given text (case-sensitive).' },
  { name: 'TEXT', syntax: 'TEXT(value)', description: 'A number, date or checkbox as text (dates as YYYY-MM-DD).' },
  { name: 'TODAY', syntax: 'TODAY()', description: 'Today’s date (Riyadh).' },
  { name: 'YEAR', syntax: 'YEAR(date)', description: 'The year of a date.' },
  { name: 'MONTH', syntax: 'MONTH(date)', description: 'The month of a date (1–12).' },
  { name: 'DAY', syntax: 'DAY(date)', description: 'The day of the month (1–31).' },
  { name: 'ADDMONTHS', syntax: 'ADDMONTHS(date, months)', description: 'Adds whole months; the 31st becomes the last day of a shorter month.' },
  { name: 'ISNEW', syntax: 'ISNEW()', description: 'True when the record is being created.', validationOnly: true },
  { name: 'ISCHANGED', syntax: 'ISCHANGED(field)', description: 'True when the save changes the field.', validationOnly: true },
  { name: 'PRIORVALUE', syntax: 'PRIORVALUE(field)', description: 'The field’s value before this save (blank for new records).', validationOnly: true },
];
const FUNCTION_NAMES = new Set(FORMULA_FUNCTIONS.map((f) => f.name));

// ---------------------------------------------------------------------------
// Type checking
// ---------------------------------------------------------------------------

/** The formula type a field's values have. */
export function formulaTypeOf(t: FieldType): FormulaType {
  if (t === 'number' || t === 'currency' || t === 'percent') return 'number';
  if (t === 'date' || t === 'datetime') return 'date';
  if (t === 'boolean') return 'boolean';
  return 'text';
}

export function returnTypeCategory(r: FormulaReturnType): FormulaType {
  return r === 'currency' || r === 'percent' ? 'number' : r;
}

const TYPE_LABEL: Record<FormulaType, string> = { number: 'a number', text: 'text', date: 'a date', boolean: 'true/false' };

export interface CheckOptions {
  /** Looks up a field by API name; formula fields must come back with their own `formula.ast` resolved. */
  field: (name: string) => FieldDef | undefined;
  /** Validation rules may use ISNEW, ISCHANGED and PRIORVALUE. */
  mode: 'field' | 'validation';
}

function nodeSize(n: FormulaNode): number {
  switch (n.k) {
    case 'num': case 'str': case 'bool': return 1;
    case 'field': return 1 + (n.field.formula?.size ?? 0);
    case 'neg': return 1 + nodeSize(n.a);
    case 'bin': return 1 + nodeSize(n.a) + nodeSize(n.b);
    case 'call': return 1 + n.args.reduce((s, a) => s + nodeSize(a), 0);
  }
}

function check(r: Raw, o: CheckOptions): FormulaNode {
  const want = (n: FormulaNode, t: FormulaType, what: string, p: number) => {
    if (n.t !== t) throw new FormulaError(`${what} needs ${TYPE_LABEL[t]}, not ${TYPE_LABEL[n.t]}`, p);
    return n;
  };
  const intLiteral = (n: Raw, min: number, max: number, what: string): number => {
    const v = n.k === 'num' ? n.v : n.k === 'neg' && n.a.k === 'num' ? -n.a.v : NaN;
    if (!Number.isInteger(v) || v < min || v > max) throw new FormulaError(`${what} must be a whole number from ${min} to ${max}`, n.p);
    return v;
  };
  switch (r.k) {
    case 'num': return { k: 'num', v: r.v, t: 'number' };
    case 'str': return { k: 'str', v: r.v, t: 'text' };
    case 'bool': return { k: 'bool', v: r.v, t: 'boolean' };
    case 'field': {
      const f = o.field(r.name);
      if (!f) throw new FormulaError(`Unknown field "${r.name}"`, r.p);
      if (f.formula && !f.formula.ast) throw new FormulaError(`The formula field "${r.name}" has an error`, r.p);
      const t = f.formula ? returnTypeCategory(f.formula.returnType) : formulaTypeOf(f.type);
      return { k: 'field', name: f.name, t, field: f };
    }
    case 'neg': return { k: 'neg', a: want(check(r.a, o), 'number', 'A minus sign', r.p), t: 'number' };
    case 'bin': {
      const a = check(r.a, o);
      const b = check(r.b, o);
      switch (r.op) {
        case '+':
          if (a.t === 'date' && b.t === 'number') return { k: 'bin', op: '+', a, b, t: 'date' };
          if (a.t === 'number' && b.t === 'date') return { k: 'bin', op: '+', a: b, b: a, t: 'date' };
          if (a.t === 'text' || b.t === 'text') throw new FormulaError('Join text with &, not +', r.p);
          want(a, 'number', '+', r.p); want(b, 'number', '+', r.p);
          return { k: 'bin', op: '+', a, b, t: 'number' };
        case '-':
          if (a.t === 'date' && b.t === 'date') return { k: 'bin', op: '-', a, b, t: 'number' };
          if (a.t === 'date') { want(b, 'number', 'Subtracting from a date', r.p); return { k: 'bin', op: '-', a, b, t: 'date' }; }
          want(a, 'number', '-', r.p); want(b, 'number', '-', r.p);
          return { k: 'bin', op: '-', a, b, t: 'number' };
        case '*': case '/':
          want(a, 'number', r.op, r.p); want(b, 'number', r.op, r.p);
          return { k: 'bin', op: r.op, a, b, t: 'number' };
        case '&':
          want(a, 'text', '&', r.p); want(b, 'text', '&', r.p);
          return { k: 'bin', op: '&', a, b, t: 'text' };
        case '=': case '<>':
          if (a.t !== b.t) throw new FormulaError(`Can't compare ${TYPE_LABEL[a.t]} with ${TYPE_LABEL[b.t]}`, r.p);
          return { k: 'bin', op: r.op, a, b, t: 'boolean' };
        default:
          if (a.t !== b.t || (a.t !== 'number' && a.t !== 'date')) throw new FormulaError(`"${r.op}" compares two numbers or two dates`, r.p);
          return { k: 'bin', op: r.op, a, b, t: 'boolean' };
      }
    }
    case 'call': {
      const fn = r.fn;
      if (!FUNCTION_NAMES.has(fn)) throw new FormulaError(`Unknown function ${fn}()`, r.p);
      const doc = FORMULA_FUNCTIONS.find((d) => d.name === fn)!;
      if (doc.validationOnly && o.mode !== 'validation') throw new FormulaError(`${fn}() can only be used in validation rules`, r.p);
      const arity = (min: number, max = min) => {
        if (r.args.length < min || r.args.length > max) {
          throw new FormulaError(min === max ? `${fn}() takes ${min} argument${min === 1 ? '' : 's'}` : `${fn}() takes ${min} to ${max} arguments`, r.p);
        }
      };
      const call = (args: FormulaNode[], t: FormulaType): FormulaNode => ({ k: 'call', fn, args, t });
      const arg = (i: number) => check(r.args[i]!, o);
      switch (fn) {
        case 'IF': {
          arity(3);
          const c = want(arg(0), 'boolean', 'The condition of IF()', r.p);
          const a = arg(1); const b = arg(2);
          if (a.t !== b.t) throw new FormulaError(`Both results of IF() must have the same type (${TYPE_LABEL[a.t]} and ${TYPE_LABEL[b.t]})`, r.p);
          return call([c, a, b], a.t);
        }
        case 'AND': case 'OR': {
          arity(1, 20);
          return call(r.args.map((_, i) => want(arg(i), 'boolean', `${fn}()`, r.p)), 'boolean');
        }
        case 'NOT': arity(1); return call([want(arg(0), 'boolean', 'NOT()', r.p)], 'boolean');
        case 'ISBLANK': arity(1); return call([arg(0)], 'boolean');
        case 'BLANKVALUE': {
          arity(2);
          const a = arg(0); const b = arg(1);
          if (a.t !== b.t) throw new FormulaError('BLANKVALUE() needs a fallback of the same type', r.p);
          return call([a, b], a.t);
        }
        case 'ROUND': {
          arity(2);
          const digits = intLiteral(r.args[1]!, 0, 10, 'The digits of ROUND()');
          return call([want(arg(0), 'number', 'ROUND()', r.p), { k: 'num', v: digits, t: 'number' }], 'number');
        }
        case 'FLOOR': case 'CEILING': case 'ABS': arity(1); return call([want(arg(0), 'number', `${fn}()`, r.p)], 'number');
        case 'MOD': arity(2); return call([want(arg(0), 'number', 'MOD()', r.p), want(arg(1), 'number', 'MOD()', r.p)], 'number');
        case 'MIN': case 'MAX': {
          arity(2, 20);
          const args = r.args.map((_, i) => arg(i));
          const t = args[0]!.t;
          if ((t !== 'number' && t !== 'date') || args.some((a) => a.t !== t)) throw new FormulaError(`${fn}() compares numbers or dates of one type`, r.p);
          return call(args, t);
        }
        case 'LEN': arity(1); return call([want(arg(0), 'text', 'LEN()', r.p)], 'number');
        case 'UPPER': case 'LOWER': case 'TRIM': arity(1); return call([want(arg(0), 'text', `${fn}()`, r.p)], 'text');
        case 'LEFT': case 'RIGHT': {
          arity(2);
          const count = intLiteral(r.args[1]!, 0, 1000, `The count of ${fn}()`);
          return call([want(arg(0), 'text', `${fn}()`, r.p), { k: 'num', v: count, t: 'number' }], 'text');
        }
        case 'CONTAINS': case 'BEGINS': arity(2); return call([want(arg(0), 'text', `${fn}()`, r.p), want(arg(1), 'text', `${fn}()`, r.p)], 'boolean');
        case 'TEXT': arity(1); return call([arg(0)], 'text');
        case 'TODAY': arity(0); return call([], 'date');
        case 'YEAR': case 'MONTH': case 'DAY': arity(1); return call([want(arg(0), 'date', `${fn}()`, r.p)], 'number');
        case 'ADDMONTHS': {
          arity(2);
          const months = intLiteral(r.args[1]!, -1200, 1200, 'The months of ADDMONTHS()');
          return call([want(arg(0), 'date', 'ADDMONTHS()', r.p), { k: 'num', v: months, t: 'number' }], 'date');
        }
        case 'ISNEW': arity(0); return call([], 'boolean');
        case 'ISCHANGED': case 'PRIORVALUE': {
          arity(1);
          const a = arg(0);
          if (a.k !== 'field' || a.field.formula || a.field.rollup) throw new FormulaError(`${fn}() takes a field that people edit`, r.p);
          return call([a], fn === 'ISCHANGED' ? 'boolean' : a.t);
        }
      }
      throw new FormulaError(`Unknown function ${fn}()`, r.p);
    }
  }
}

/** Parses and type-checks an expression. Throws FormulaError with a readable message. */
export function compileFormula(expression: string, o: CheckOptions): { ast: FormulaNode; size: number } {
  const ast = check(parse(expression), o);
  const size = nodeSize(ast);
  if (size > FORMULA_MAX_NODES) throw new FormulaError('The formula is too large (including the formulas it uses)');
  return { ast, size };
}

/** Field names a typed tree reads directly (not through other formulas). */
export function formulaFieldNames(n: FormulaNode, out = new Set<string>()): Set<string> {
  if (n.k === 'field') out.add(n.name);
  else if (n.k === 'neg') formulaFieldNames(n.a, out);
  else if (n.k === 'bin') { formulaFieldNames(n.a, out); formulaFieldNames(n.b, out); }
  else if (n.k === 'call') for (const a of n.args) formulaFieldNames(a, out);
  return out;
}

/** Field API names an expression mentions, without type checking (for "used by" checks). */
export function referencedNames(expression: string): Set<string> {
  try {
    const out = new Set<string>();
    const walk = (r: Raw) => {
      if (r.k === 'field') out.add(r.name);
      else if (r.k === 'neg') walk(r.a);
      else if (r.k === 'bin') { walk(r.a); walk(r.b); }
      else if (r.k === 'call') r.args.forEach(walk);
    };
    walk(parse(expression));
    return out;
  } catch {
    return new Set();
  }
}

/**
 * Resolves every formula field in a field list: each gets `formula.ast` (or `formula.error`).
 * Formulas may use other formulas (up to FORMULA_MAX_CHAIN deep); cycles are errors.
 */
export function resolveFormulaFields(fields: FieldDef[]): FieldDef[] {
  const byName = new Map(fields.map((f) => [f.name, f]));
  const done = new Map<string, FieldDef>();
  const resolve = (name: string, stack: string[]): FieldDef | undefined => {
    const f = byName.get(name);
    if (!f?.formula) return f;
    if (done.has(name)) return done.get(name);
    if (stack.includes(name)) throw new FormulaError(`Formulas can't refer to each other in a loop (${[...stack, name].join(' → ')})`);
    if (stack.length >= FORMULA_MAX_CHAIN) throw new FormulaError(`Formulas can refer to other formulas at most ${FORMULA_MAX_CHAIN} levels deep`);
    let info: FormulaInfo;
    try {
      const { ast, size } = compileFormula(f.formula.expression, { mode: 'field', field: (n) => resolve(n, [...stack, name]) });
      const got = returnTypeCategory(f.formula.returnType);
      if (ast.t !== got) throw new FormulaError(`The formula gives ${TYPE_LABEL[ast.t]}, but the field is set to return ${TYPE_LABEL[got]}`);
      info = { expression: f.formula.expression, returnType: f.formula.returnType, ast, size };
    } catch (e) {
      // A loop is reported on the field that started it; the fields inside it get their own error.
      if (stack.length && e instanceof FormulaError && e.message.startsWith('Formulas can')) throw e;
      info = { expression: f.formula.expression, returnType: f.formula.returnType, error: e instanceof Error ? e.message : String(e) };
    }
    const out: FieldDef = { ...f, formula: info };
    done.set(name, out);
    return out;
  };
  return fields.map((f) => (f.formula ? resolve(f.name, [])! : f));
}

// ---------------------------------------------------------------------------
// Evaluation in memory (mirrors the SQL the API generates)
// ---------------------------------------------------------------------------

export interface EvalContext {
  /** Values before this save (validation rules); null when creating. */
  prior?: Record<string, unknown> | null;
  /** Override for tests: today's date as YYYY-MM-DD (Riyadh). */
  today?: string;
}

type V = number | string | boolean | null;

/** The Riyadh calendar day of a moment (UTC+3 all year), as the database computes it: formulas agree with the rest of the app. */
const riyadhDayOf = (d: Date) => new Date(d.getTime() + 3 * 3600_000).toISOString().slice(0, 10);
const toDate = (v: unknown): string | null => {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : riyadhDayOf(v);
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? (s.length > 10 ? riyadhDayOf(new Date(s)) : s) : null;
};

/** A record value as the formula type sees it. */
export function formulaValue(f: FieldDef, raw: unknown): V {
  if (raw === null || raw === undefined) return null;
  switch (f.formula ? returnTypeCategory(f.formula.returnType) : formulaTypeOf(f.type)) {
    case 'number': { const n = typeof raw === 'number' ? raw : Number(raw); return Number.isFinite(n) ? n : null; }
    case 'date': return toDate(raw);
    case 'boolean': return typeof raw === 'boolean' ? raw : raw === 'true' ? true : raw === 'false' ? false : null;
    default: return String(raw);
  }
}

function addDaysIso(d: string, n: number): string | null {
  if (Math.abs(n) > FORMULA_MAX_DAY_OFFSET) return null;
  const t = Date.parse(`${d}T00:00:00Z`) + Math.trunc(n) * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

function addMonthsIso(d: string, k: number): string {
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + k;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12;
  const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  return new Date(Date.UTC(ny, nm, Math.min(day, last))).toISOString().slice(0, 10);
}

/**
 * Rounds half away from zero, like Postgres numeric round(). Binary floating-point noise is removed
 * first (1234.5 * 0.15 is 185.17499999999998 in JS but exactly 185.175 in Postgres).
 */
export function roundHalfAway(x: number, digits: number): number {
  const abs = Number(Math.abs(x).toPrecision(15));
  const s = String(abs);
  const v = s.includes('e') ? Math.round(abs * 10 ** digits) / 10 ** digits : Number(`${Math.round(Number(`${s}e${digits}`))}e-${digits}`);
  return x < 0 ? -v : v;
}

const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000);

/** Evaluates a typed tree against a record keyed by field API name. */
export function evaluateFormula(n: FormulaNode, rec: Record<string, unknown>, ctx: EvalContext = {}): V {
  const ev = (x: FormulaNode) => evaluateFormula(x, rec, ctx);
  switch (n.k) {
    case 'num': case 'str': case 'bool': return n.v;
    case 'field': {
      if (n.field.formula?.ast) return ev(n.field.formula.ast);
      return formulaValue(n.field, rec[n.name]);
    }
    case 'neg': { const a = ev(n.a); return a === null ? null : -(a as number); }
    case 'bin': {
      const a = ev(n.a); const b = ev(n.b);
      if (n.op === '&') return `${a ?? ''}${b ?? ''}`;
      if (a === null || b === null) return null;
      switch (n.op) {
        case '+': return n.t === 'date' ? addDaysIso(a as string, b as number) : (a as number) + (b as number);
        case '-':
          if (n.a.t === 'date' && n.b.t === 'date') return daysBetween(a as string, b as string);
          return n.t === 'date' ? addDaysIso(a as string, -(b as number)) : (a as number) - (b as number);
        case '*': return (a as number) * (b as number);
        case '/': return b === 0 ? null : (a as number) / (b as number);
        case '=': return a === b;
        case '<>': return a !== b;
        // Both sides have one type (numbers, or YYYY-MM-DD dates that sort as text).
        case '<': return (a as number) < (b as number);
        case '<=': return (a as number) <= (b as number);
        case '>': return (a as number) > (b as number);
        case '>=': return (a as number) >= (b as number);
      }
      return null;
    }
    case 'call': {
      const args = n.args;
      switch (n.fn) {
        case 'IF': return ev(args[0]!) === true ? ev(args[1]!) : ev(args[2]!);
        case 'AND': {
          const vs = args.map(ev);
          return vs.includes(false) ? false : vs.includes(null) ? null : true;
        }
        case 'OR': {
          const vs = args.map(ev);
          return vs.includes(true) ? true : vs.includes(null) ? null : false;
        }
        case 'NOT': { const a = ev(args[0]!); return a === null ? null : !a; }
        case 'ISBLANK': { const a = ev(args[0]!); return a === null || a === ''; }
        case 'BLANKVALUE': { const a = ev(args[0]!); return a === null || a === '' ? ev(args[1]!) : a; }
        case 'ROUND': { const a = ev(args[0]!); return a === null ? null : roundHalfAway(a as number, (args[1] as { v: number }).v); }
        case 'FLOOR': { const a = ev(args[0]!); return a === null ? null : Math.floor(a as number); }
        case 'CEILING': { const a = ev(args[0]!); return a === null ? null : Math.ceil(a as number); }
        case 'ABS': { const a = ev(args[0]!); return a === null ? null : Math.abs(a as number); }
        case 'MOD': {
          const a = ev(args[0]!); const b = ev(args[1]!);
          return a === null || b === null || b === 0 ? null : (a as number) % (b as number);
        }
        case 'MIN': case 'MAX': {
          const vs = args.map(ev).filter((v): v is number | string => v !== null) as (number | string)[];
          if (!vs.length) return null;
          return vs.reduce((m, v) => ((n.fn === 'MIN' ? (v as number) < (m as number) : (v as number) > (m as number)) ? v : m));
        }
        case 'LEN': { const a = ev(args[0]!); return [...String(a ?? '')].length; }
        case 'UPPER': { const a = ev(args[0]!); return a === null ? null : String(a).toUpperCase(); }
        case 'LOWER': { const a = ev(args[0]!); return a === null ? null : String(a).toLowerCase(); }
        case 'TRIM': { const a = ev(args[0]!); return a === null ? null : String(a).replace(/^ +| +$/g, ''); }
        case 'LEFT': case 'RIGHT': {
          const a = ev(args[0]!);
          if (a === null) return null;
          const chars = [...String(a)];
          const c = (args[1] as { v: number }).v;
          return n.fn === 'LEFT' ? chars.slice(0, c).join('') : c === 0 ? '' : chars.slice(-c).join('');
        }
        case 'CONTAINS': case 'BEGINS': {
          const a = ev(args[0]!); const b = ev(args[1]!);
          if (a === null || b === null) return null;
          return n.fn === 'CONTAINS' ? String(a).includes(String(b)) : String(a).startsWith(String(b));
        }
        case 'TEXT': {
          const a = ev(args[0]!);
          if (a === null) return null;
          return typeof a === 'boolean' ? (a ? 'true' : 'false') : String(a);
        }
        case 'TODAY': return ctx.today ?? riyadhDayOf(new Date());
        case 'YEAR': case 'MONTH': case 'DAY': {
          const a = ev(args[0]!);
          if (a === null) return null;
          const [y, m, d] = String(a).split('-').map(Number);
          return n.fn === 'YEAR' ? y! : n.fn === 'MONTH' ? m! : d!;
        }
        case 'ADDMONTHS': { const a = ev(args[0]!); return a === null ? null : addMonthsIso(a as string, (args[1] as { v: number }).v); }
        case 'ISNEW': return !ctx.prior;
        case 'ISCHANGED': {
          const f = args[0] as Extract<FormulaNode, { k: 'field' }>;
          if (!ctx.prior) return false;
          return formulaValue(f.field, ctx.prior[f.name]) !== formulaValue(f.field, rec[f.name]);
        }
        case 'PRIORVALUE': {
          const f = args[0] as Extract<FormulaNode, { k: 'field' }>;
          return ctx.prior ? formulaValue(f.field, ctx.prior[f.name]) : null;
        }
      }
      return null;
    }
  }
}

/** Sets every formula field's value on an in-memory record (roll-ups keep whatever value the record has). */
export function applyFormulas(fields: readonly FieldDef[], rec: Record<string, unknown>): Record<string, unknown> {
  for (const f of fields) if (f.formula) rec[f.name] = f.formula.ast ? evaluateFormula(f.formula.ast, rec) : null;
  return rec;
}

/** Removes compiled trees from a schema before it is sent to browsers (they only need the expression). */
export function withoutFormulaTrees<T extends { fields: readonly FieldDef[] }>(obj: T): T {
  if (!obj.fields.some((f) => f.formula?.ast)) return obj;
  return {
    ...obj,
    fields: obj.fields.map((f) => (f.formula ? { ...f, formula: { expression: f.formula.expression, returnType: f.formula.returnType, ...(f.formula.error ? { error: f.formula.error } : {}) } } : f)),
  } as T;
}
